import { writable } from "svelte/store";

export const user = writable(null);
export const app = writable(null);
export const notices = writable(null);
export const analytics = writable({
  android: 0,
  ios: 0,
  prev: 0,
});
export let customizeData = writable({
  data: "",
});

let userCopy;
user.subscribe((value) => {
  userCopy = value;
});
const VITE_API_LINK = "https://api.qix.cloud/";
const VITE_AUTH_M_REDIRECT_URI = "https://api.qix.cloud/microsoftRedirectSign";
const VITE_AUTH_REDIRECT_URI = "https://api.qix.cloud/googleRedirectSign";
//const VITE_BASE_LINK = "https://qixcloud-chat.pages.dev";
const VITE_ORIGIN_LINK = "https://legalpal.app/sign";

function setAuthSessionCookie(token) {
  if (typeof document === "undefined" || !token) return;
  const maxAgeSeconds = 30 * 24 * 60 * 60;
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host.endsWith(".pages.dev");
  const domainAttr = isLocal ? "" : "; domain=.legalpal.app";
  document.cookie = `lp_token=${encodeURIComponent(token)}; path=/; max-age=${maxAgeSeconds}; Secure; SameSite=Lax${domainAttr}`;
}

function readAuthSessionCookie() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|; )lp_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function clearAuthSessionCookie() {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host.endsWith(".pages.dev");
  const domainAttr = isLocal ? "" : "; domain=.legalpal.app";
  document.cookie = `lp_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}`;
}
/**
 * Removes auth token/code query parameters from the browser address bar
 * without causing a page reload.
 */
export const cleanAuthUrlParams = () => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let changed = false;

    let cleanPathname = url.pathname;
    if (cleanPathname.length > 1 && cleanPathname.endsWith("/")) {
      cleanPathname = cleanPathname.replace(/\/+$/, "");
      changed = true;
    }

    const authKeys = [
      "iss",
      "rtnk",
      "token",
      "code",
      "session_state",
      "state",
      "scope",
      "authuser",
      "prompt",
      "hd",
      "client_info",
      "id_token",
      "access_token",
    ];
    authKeys.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      const searchStr = url.searchParams.toString();
      const cleanPath =
        cleanPathname + (searchStr ? `?${searchStr}` : "") + url.hash;
      window.history.replaceState({}, document.title, cleanPath);

      if (window.parent && window.parent !== window) {
        try {
          const parentUrl = new URL(window.parent.location.href);
          let parentChanged = false;
          let parentCleanPathname = parentUrl.pathname;
          if (parentCleanPathname.length > 1 && parentCleanPathname.endsWith("/")) {
            parentCleanPathname = parentCleanPathname.replace(/\/+$/, "");
            parentChanged = true;
          }
          authKeys.forEach((key) => {
            if (parentUrl.searchParams.has(key)) {
              parentUrl.searchParams.delete(key);
              parentChanged = true;
            }
          });
          if (parentChanged) {
            const parentSearch = parentUrl.searchParams.toString();
            const parentCleanPath =
              parentCleanPathname +
              (parentSearch ? `?${parentSearch}` : "") +
              parentUrl.hash;
            window.parent.history.replaceState(
              {},
              document.title,
              parentCleanPath
            );
          }
        } catch (e) {
          // Ignore cross-origin frame restrictions
        }
      }
    }
  } catch (err) {
    console.warn("Could not clean auth URL params:", err);
  }
};

export const auth = () => {
  return new Promise((resolve, reject) => {
    const urlParams = new URLSearchParams(window.location.search);
    let code = urlParams.get("token") || urlParams.get("code");
    let rtnk = urlParams.get("rtnk");

    if (!rtnk && !code && typeof window !== "undefined" && window.parent && window.parent !== window) {
      try {
        const parentParams = new URLSearchParams(window.parent.location.search);
        rtnk = parentParams.get("rtnk");
        code = parentParams.get("token") || parentParams.get("code");
      } catch (e) {
        // Cross-origin fallback
      }
    }

    if (rtnk) {
      localStorage.setItem("token", rtnk);
      setAuthSessionCookie(rtnk);
    } else if (!localStorage.getItem("token")) {
      const shared = readAuthSessionCookie();
      if (shared) localStorage.setItem("token", shared);
    }

    cleanAuthUrlParams();

    if (code && code.length) {
      if (code.includes("microsoft_code_")) {
        code = code.replace("microsoft_code_", "");
        getTokenWithMicrosoftCode(code)
          .then((token) => {
            const tokenStr = typeof token === "string" ? token : token?.token || "";
            if (tokenStr) {
              localStorage.setItem("token", tokenStr);
              setAuthSessionCookie(tokenStr);
            }
            return getUserInfo(tokenStr);
          })
          .then((info) => {
            user.set(info);
            resolve(info);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      } else {
        getTokenWithGoogleCode(code)
          .then((token) => {
            const tokenStr = typeof token === "string" ? token : token?.token || "";
            if (tokenStr) {
              localStorage.setItem("token", tokenStr);
              setAuthSessionCookie(tokenStr);
            }
            return getUserInfo(tokenStr);
          })
          .then((info) => {
            user.set(info);
            resolve(info);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      }
    } else if (localStorage.getItem("token")) {
      const token = localStorage.getItem("token") || "";
      getUserInfo(token)
        .then((info) => {
          let signinCount = localStorage.getItem("signinCount")
            ? localStorage.getItem("signinCount")
            : 0;
          if (signinCount >= 5) signinCount = 0;
          signinCount++;
          localStorage.setItem("signinCount", signinCount);
          info.signinCount = signinCount;
          user.set(info);
          resolve(info);
        })
        .catch((err) => {
          console.error("Token validation error in pdf-editor:", err);
          localStorage.removeItem("token");
          user.set(null);
          reject(err);
        });
    } else {
      resolve();
    }
  });
};

export const logout = () => {
  user.set(null);
  localStorage.removeItem("token");
  clearAuthSessionCookie();
  location.reload();
};

export const signInWithGoogle = () => {
  const rootUrl = "https://accounts.google.com/o/oauth2/v2/auth";
  const options = {
    redirect_uri: VITE_AUTH_REDIRECT_URI,
    client_id:
      "567227164069-n724qdj6qo5ri7u2dt922j8o6f536027.apps.googleusercontent.com",
    access_type: "offline",
    response_type: "code",
    prompt: "consent",
    scope: [
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/calendar.app.created",
      "https://www.googleapis.com/auth/drive.file",
      "https://www.googleapis.com/auth/gmail.send",
    ].join(" "),
  };

  const params = new URLSearchParams(options);
  const state = productAuthState();
  if (state) params.set("state", state);
  const url = `${rootUrl}?${params.toString()}`;

  goAuth(url);
};

function productAuthState() {
  const host = window.location.hostname;
  const allowed = ["chat.legalpal.app", "sign.legalpal.app", "app.legalpal.app", "account.legalpal.app", "client.legalpal.app"];
  if (!allowed.includes(host)) return "";
  return btoa(JSON.stringify({ r: "https://" + host })).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function goAuth(url) {
  const host = window.location.hostname;
  const allowed = ["chat.legalpal.app", "sign.legalpal.app", "app.legalpal.app", "account.legalpal.app", "client.legalpal.app"];
  if (allowed.includes(host)) window.location.href = url;
  else window.parent.location.href = url;
}

// export const signInWithMicrosoft = () => {
//   const rootUrl = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

//   const options = {
//     client_id: "7b0ca8a3-0bc1-4fce-885d-8428d3b98aac",
//     redirect_uri: VITE_AUTH_M_REDIRECT_URI,
//     response_type: "code",
//     prompt: "consent",
//     scope: [
//       "openid",
//       "profile",
//       "email",
//       "offline_access",
//       "User.Read",
//       "Calendars.ReadWrite",
//       "Files.ReadWrite",
//       "Files.ReadWrite.All",
//       "Sites.ReadWrite.All",
//       "Sites.FullControl.All",
//       "SharePointTenantSettings.ReadWrite.All",
//       "Mail.Send",
//       "Mail.Send.Shared"
//     ].join(" ")
//   };

//   const url = `${rootUrl}?${new URLSearchParams(options).toString()}`;
//   window.parent.location.href = url;
// };

export const signInWithMicrosoft = () => {
  const rootUrl = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

  const options = {
    client_id: "7b0ca8a3-0bc1-4fce-885d-8428d3b98aac",
    redirect_uri: VITE_AUTH_M_REDIRECT_URI,
    response_type: "code",
    prompt: "select_account",
    scope: [
      "openid",
      "profile",
      "email",
      "offline_access",
      "User.Read"
    ].join(" ")
  };

  const params = new URLSearchParams(options);
  const state = productAuthState();
  if (state) params.set("state", state);
  const url = `${rootUrl}?${params.toString()}`;
  goAuth(url);
};


const getTokenWithGoogleCode = (code) => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "sign/google", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code,
        redirectUri: VITE_AUTH_REDIRECT_URI,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

const getTokenWithMicrosoftCode = (code) => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "sign/microsoft", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code,
        redirect_uri: VITE_AUTH_M_REDIRECT_URI,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

const getUserInfo = (token) => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "auth", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token,
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const getChatUsers = () => {
  return new Promise((resolve, reject) => {
    if (localStorage.getItem("token") == null) {
      resolve([]);
    } else {
      console.log("getChatUsers");
      fetch(VITE_API_LINK + "chats?status=active&full=true&userId="+userCopy.id, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      })
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          resolve(res);
        })
        .catch(() => {
          reject();
        });
    }
  });
};

export const getAnalytics = () => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "analytics", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        analytics.set({
          android: res.android,
          ios: res.ios,
          prev: res.prev,
        });
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
};

export const sendBase64FileToGoogleDrive = (file, fileMimeType) => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "sendGoogleDriveBase64File", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        file,
        fileMimeType,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res ? res : "Error");
      })
      .catch(() => {
        reject();
      });
  });
};

export const sendBase64FileToOneDrive = (file, fileMimeType) => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "sendOneDriveBase64File", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        file,
        fileMimeType,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res ? res : "Error");
      })
      .catch(() => {
        reject();
      });
  });
};
export const getTeam = () => {
  return new Promise((resolve, reject) => {
    if (localStorage.getItem("token") == null) {
      resolve([]);
    } else {
      fetch(VITE_API_LINK + "team", {
        method: "GET",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      })
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          //team.set(res);
          resolve(res);
        })
        .catch(() => {
          reject();
        });
    }
  });
};
